import dayjs from 'dayjs';
import { useCallback, useEffect } from 'react';

/**
 * Calls onStale when the window regains focus or the tab becomes visible, if more than maxAgeMs has passed since the
 * load time stored under storageKey.
 * @returns onSetLoadTime to record a fresh load
 */
const useRefreshWhenStale = ({
  storageKey,
  maxAgeMs,
  onStale,
}: {
  storageKey: string;
  maxAgeMs: number;
  onStale: () => void;
}) => {
  const handleSetLoadTime = useCallback(() => {
    localStorage.setItem(storageKey, dayjs().toISOString());
  }, [storageKey]);

  const validateRefetch = useCallback(() => {
    const previousLoadTime = localStorage.getItem(storageKey);
    if (previousLoadTime && dayjs().diff(dayjs(previousLoadTime), 'milliseconds') > maxAgeMs) {
      onStale();
      handleSetLoadTime();
    }
  }, [storageKey, maxAgeMs, onStale, handleSetLoadTime]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) validateRefetch();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', validateRefetch);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', validateRefetch);
    };
  }, [validateRefetch]);

  return { onSetLoadTime: handleSetLoadTime };
};

export default useRefreshWhenStale;
